import {
  $lang,
  useEntityBaseMetadata,
  useEntityMetadata,
  useMessagesMetadata,
  useRuleEngineCache,
  useTranslation,
} from '@msdk/common'
import {
  BaseValidationDefinition,
  EntityMetadata,
  FieldDefinition,
  FormFieldDefinition,
  FormNode,
  MessageModel,
  NumericFieldDefinition,
  NumericValue,
  RangeValidationDefinition,
  Severity,
  tryConvert,
  tryConvertDecimalInfo,
} from '@msdk/definitions'
import { COLORS, createComponentWrapperWithRef, FormHelperText, IconSelector, styled, uiTokens } from '@msdk/ui'
import React from 'react'
import { useRecoilValue } from 'recoil'

import { stringFormat } from '../utils'

const StyledMuiFormHelperText = styled(FormHelperText, { shouldForwardProp: (prop) => prop !== '$severity' })<{
  $severity: Severity
}>`
  ${(props) => props.$severity === Severity.Error && `color: ${COLORS.validation.error.main}`}
  ${(props) => props.$severity === Severity.Warning && `color: ${COLORS.validation.warning.main}`}
  ${(props) => props.$severity === Severity.Info && `color: ${COLORS.validation.info.main}`}
  ${(props) => props.$severity === Severity.None && `color: ${COLORS.validation.none.main}`}
`
const StyledNovaFormHelperText = styled(FormHelperText, { shouldForwardProp: (prop) => prop !== '$severity' })<{
  $severity: Severity
}>`
  font-family: ${uiTokens.font.fontFamily.default};
  display: flex;
  align-items: flex-start;
  gap: ${uiTokens.spacing.gap.xxSmall}px;
  ${(props) => props.$severity === Severity.Error && `color: ${COLORS.validation.error.main}`}
  ${(props) => props.$severity === Severity.Warning && `color: ${COLORS.validation.warning.main}`}
  ${(props) => props.$severity === Severity.Info && `color: ${COLORS.validation.info.main}`}
  ${(props) => props.$severity === Severity.None && `color: ${COLORS.validation.none.main}`}
`

const StyledFormHelperText = createComponentWrapperWithRef(
  {
    mui: StyledMuiFormHelperText,
    nova: StyledNovaFormHelperText,
  },
  'StyledFormHelperText',
)

const NovaIconSelector = styled(IconSelector)`
  display: flex;
  width: ${uiTokens.sizing.icon.xSmall}px;
  height: ${uiTokens.sizing.icon.xSmall}px;
  justify-content: center;
`

const StyledIconSelector = createComponentWrapperWithRef(
  {
    mui: () => null,
    nova: NovaIconSelector,
  },
  'StyledIconSelector',
)

interface Props {
  messages: string[]
  fieldDef: FieldDefinition
  node?: FormNode
}

export function ValidationMessages(props: Props) {
  const t = useTranslation()
  const formDef = props.node?.value.formDefinition as FormFieldDefinition
  const baseMetadata = useEntityBaseMetadata(props.node?.value.entityName as string) as EntityMetadata
  const entityModel = useEntityMetadata(props.node?.value.entityName as string)
  const lang = useRecoilValue($lang)

  const rootFormName = props.node?.value.rootFormModelName as string
  const { cachedValidation } = useRuleEngineCache(props.node?.value.entityName as string, rootFormName)

  const messages = useMessagesMetadata(baseMetadata?.msProvider)
  const msgToShow = (messages || []).filter((m) => props.messages.includes(m.code))

  const formatDefaultDescription = (m: MessageModel): string => {
    if (m.type === Severity.Mandatory) {
      return ''
    }

    const params = props.node?.value?.validationParams ?? []

    // Params from server side validations already contain the field description as first parameter
    // for the message, but client side ones don't and since in some places it is hard to whire
    // the translation hook in some places this is here to prevent parameter duplication
    const fieldDesc = t(props.fieldDef.idml, props.fieldDef.description)
    if (params.length && fieldDesc === params[0]) {
      params.splice(0, 1)
    }

    return stringFormat(t(m.idml, m.description), t(props.fieldDef.idml, props.fieldDef.description), ...params)
  }

  const formatDescription = (m: MessageModel) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const possibleMessages = (cachedValidation.cachedRules[formDef?.name] ?? []).filter((v) => v.msgCode === m.code)
    let v: BaseValidationDefinition | undefined
    if (
      possibleMessages.length > 1 &&
      props.node?.value.validationParams &&
      props.node?.value.validationParams.length
    ) {
      v = possibleMessages.find((msg) => msg.msgParameters === props.node?.value.validationParams.join(','))
    } else if (possibleMessages.length >= 1) {
      v = possibleMessages[0]
    }

    if (props.node?.value.listName) {
      const listName = props.node?.value.listName as string
      const listRules = cachedValidation.cachedListRules[listName]
      const msgs = listRules.filter((r) => r.field === props.fieldDef.name && r.msgCode === m.code)
      if (msgs.length > 1 && props.node?.value.validationParams && props.node?.value.validationParams.length) {
        v = msgs.find((msg) => msg.msgParameters === props.node?.value.validationParams.join(','))
      } else if (msgs.length >= 1) {
        v = msgs[0]
      }
    }

    let formatted = formatDefaultDescription(m)

    // When the range was set only by a DataRule (no static RangeValidation in cache),
    // v is undefined but node.value.range is still populated — format it directly.
    if (!v && props.node?.value.range && (m.code === 'FRM_00006' || m.code === 'FRM_00035' || m.code === 'FRM_00036')) {
      v = { field: props.node.id, validationType: 'Range', msgCode: m.code } as RangeValidationDefinition
    }

    if (v) {
      switch (v.validationType) {
        case 'Range': {
          const r = props.node?.value.range ?? (v as RangeValidationDefinition)
          const desc = t(m.idml, m.description)
          const fieldDesc = t(props.fieldDef.idml, props.fieldDef.description)
          const minDefined = r.min !== null && r.min !== undefined
          const maxDefined = r.max !== null && r.max !== undefined

          const fieldDef = props.node?.value.fieldDefinition as NumericFieldDefinition
          const decimalInfo = tryConvertDecimalInfo(
            fieldDef.decimalInfo,
            fieldDef.um,
            fieldDef.entityName,
            fieldDef.name,
            fieldDef.step,
          )
          let convertedMin = minDefined ? tryConvert(r.min, fieldDef.um, fieldDef.entityName, fieldDef.name) : undefined
          let convertedMax = maxDefined ? tryConvert(r.max, fieldDef.um, fieldDef.entityName, fieldDef.name) : undefined

          convertedMin =
            convertedMin !== undefined ? parseFloat(convertedMin.toFixed(decimalInfo?.decimalLimit)) : undefined
          convertedMax =
            convertedMax !== undefined ? parseFloat(convertedMax.toFixed(decimalInfo?.decimalLimit)) : undefined

          const convertedMinNumeric = new NumericValue(convertedMin, undefined, undefined, decimalInfo?.decimalLimit)
          const convertedMaxNumeric = new NumericValue(convertedMax, undefined, undefined, decimalInfo?.decimalLimit)

          if (minDefined && maxDefined) {
            formatted = stringFormat(
              desc,
              fieldDesc,
              convertedMinNumeric.toStringForView(lang),
              convertedMaxNumeric.toStringForView(lang),
            )
          } else if (minDefined && !maxDefined) {
            formatted = stringFormat(desc, fieldDesc, convertedMinNumeric.toStringForView(lang))
          } else if (maxDefined && !minDefined) {
            formatted = stringFormat(desc, fieldDesc, convertedMaxNumeric.toStringForView(lang))
          }
          break
        }
        case 'Suggested':
        case 'Invisible':
        case 'Step':
        case 'Readonly':
        case 'RemoveOption':
        case 'KeepOption':
        case 'Expression':
          formatted = stringFormat(
            t(m.idml, m.description),
            t(props.fieldDef.idml, props.fieldDef.description),
            ...(v.msgParameters ?? '').split(',').map((p) => {
              if (p.includes('FE.')) {
                // Since the parameter is a form field, we need to get its definition from the form.
                const param = p.replace('FE.', '').trim()
                const formField = entityModel?.getForm(rootFormName)?.getFieldDefinition(param)
                return t(formField?.idml ?? '', formField?.description ?? '') || p
              } else {
                // In this case it's a standard field, we need to get its definition from the entity.
                const paramField = (entityModel?.fields ?? []).find((f) => f.name === p.trim())
                return t(paramField?.idml ?? '', paramField?.description ?? '') || p
              }
            }),
          )
          break
        case 'Mandatory':
          // The mandatory validation is handled differenlty from all the others
          // an asterisk will be shown near the field label instead of the normal message
          // used by all other validation types, so we do nothing here.
          break
        default:
          throw new Error('Unsupported validation type: ' + v.validationType)
      }
      // if there is no validation the message may have been added from custom logic
    }

    return formatted
  }

  return (
    <>
      {msgToShow.map((m) => (
        <StyledFormHelperText key={m.code} $severity={m.type}>
          {m.type === Severity.Error && <StyledIconSelector iconName="ErrorOutlineOutlined"></StyledIconSelector>}
          {formatDescription(m)}
        </StyledFormHelperText>
      ))}
    </>
  )
}
